import mealsImage from "@assets/icons/generic/lunch-bag.png"
import petal from "@assets/icons/generic/petal_new.png"
import treesImage from "@assets/icons/generic/tree.png"
import AvatarM from "@common/components/avatar/AvatarM"
import { BadgeM } from "@common/components/badge/BadgeM"
import { LiveBadgeM } from "@common/components/badge/LiveBadgeM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { PaginatedTableM } from "@common/components/dataTable/PaginatedTableM"
import { EngagementScoreComponent } from "@common/components/liveReports/LiveReportsScreen"
import { StatisticsItem } from "@common/components/statistics/StatisticsItem"
import LinkTextM from "@common/components/text/LinkTextM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { hasRole } from "@data/utility/ability"
import { useNavigation } from "@react-navigation/native"
import moment from "moment"
import React from "react"
import { View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import { AvailabilitySlotsM } from "../student/account/subscription/AvailabilitySlotsM"
import { PrioritizedSchoolSubjects } from "../tutor/students/ProfilDetails"

// Shown under each engagement score when the school demo runs in anonymous mode.
const fakeComments = [
    "L'élève montre une grande aisance à l'oral, notamment lors des discussions en anglais. Cependant, des exercices ritualisés de grammaire sont en place pour renforcer les bases, avec une répétition hebdomadaire pour faciliter l'assimilation.",
    "L'élève manifeste un intérêt particulier pour les sciences et participe activement aux expériences pratiques. En mathématiques, des sessions ciblées sur les fondamentaux sont organisées pour combler les lacunes et consolider la confiance.",
    "L'élève excelle dans l'analyse littéraire grâce à une réflexion pertinente. Pour renforcer les compétences en rédaction, des exercices structurés et une lecture quotidienne de textes modèles sont mis en place.",
    "L'élève s'illustre en mathématiques grâce à une rigueur dans la résolution des problèmes. En physique, un rituel hebdomadaire de résolution de cas pratiques a été instauré pour mieux intégrer les concepts abstraits.",
    "L'élève est très engagé en biologie et participe avec enthousiasme. En chimie, des exercices répétitifs sur les équations chimiques ont été instaurés pour gagner en fluidité et en compréhension.",
    "L'élève démontre une excellente mémoire en histoire, avec des récits détaillés et précis. Pour l'analyse littéraire en français, un rituel de lecture guidée et de discussions hebdomadaires a été mis en place.",
    "L'élève brille en physique-chimie avec des solutions originales aux problèmes posés. En mathématiques, il suit une approche visuelle et des exercices réguliers pour structurer davantage ses raisonnements.",
    "L'élève se distingue par une curiosité en géographie, notamment lors de projets de recherche. Pour développer la participation en anglais, des activités collaboratives hebdomadaires et des jeux de rôles sont proposés.",
]

const tutoredStudentRoles = [
    "premium-student",
    "initial-student",
    "essential-student",
    "integral-student",
]

const SchoolStudentsTable = (props) => {
    const {
        requestApi,
        routeParams,
        urlParams,
        statsPeriod = "lifetime",
        additionalHeader = <></>,
        anonymous = false,
    } = props

    const navigation = useNavigation()

    const columns = [
        {
            name: "Nom",
            style: {
                width: 240,
                marginRight: 20,
            },
            selector: "lastname",
            sortable: true,
            cell: (row) => (
                <TouchableRippleM
                    onPress={() => {
                        navigation.push("student-profil", {
                            studentId: row?.id,
                            withActions: true,
                        })
                    }}
                    style={{
                        justifyContent: "flex-start",
                        alignItems: "center",
                        flexDirection: "row",
                    }}
                >
                    <>
                        <AvatarM user={row} size={32} />

                        <View
                            style={{
                                marginLeft: 15,
                                alignItems: "flex-start",
                            }}
                        >
                            <TextM
                                color={theme.colors.primary}
                                fontWeight={"Medium"}
                                wrap
                                style={{ maxWidth: 150 }}
                            >{`${row?.firstname} ${row?.lastname}`}</TextM>
                            <TextM
                                color={theme.colors.grey}
                                fontWeight={"Light"}
                                wrap
                                fontSize={10}
                                style={{ maxWidth: 150 }}
                            >{`${row?.email}`}</TextM>
                            <View>
                                <TextM
                                    color={theme.colors.grey}
                                    fontWeight={"Light"}
                                    wrap
                                    fontSize={10}
                                    style={{ maxWidth: 150 }}
                                >
                                    Tel parent {`${row?.parent_mobile ?? "-"}`}
                                </TextM>
                                {row?.family?.name && (
                                    <TextM
                                        color={theme.colors.primary}
                                        fontWeight={"Medium"}
                                        wrap
                                        fontSize={9}
                                        style={{
                                            marginTop: 2,
                                            maxWidth: 150,
                                        }}
                                    >
                                        {row.family.name}
                                    </TextM>
                                )}
                            </View>
                            {row?.is_first_month && (
                                <BadgeM
                                    style={{
                                        backgroundColor: theme.colors.error,
                                        color: theme.colors.white,
                                        alignSelf: "flex-start",
                                        marginTop: 3,
                                    }}
                                    count={"1er mois"}
                                    size={18}
                                />
                            )}
                            {row?.is_first_month &&
                                row?.quality_watch_state &&
                                row?.quality_watch_state !== "healthy" && (
                                    <BadgeM
                                        style={{
                                            backgroundColor:
                                                row?.quality_watch_state ===
                                                "critical"
                                                    ? "#8B0000"
                                                    : row?.quality_watch_state ===
                                                        "at_risk"
                                                      ? theme.colors.error
                                                      : theme.colors.warning,
                                            color: theme.colors.white,
                                            alignSelf: "flex-start",
                                            marginTop: 3,
                                        }}
                                        count={
                                            row?.quality_watch_state ===
                                            "critical"
                                                ? "Critique"
                                                : row?.quality_watch_state ===
                                                    "at_risk"
                                                  ? "À risque"
                                                  : "Vigilance"
                                        }
                                        size={18}
                                    />
                                )}
                            <BadgeM
                                style={{
                                    backgroundColor:
                                        row?.subscription_state?.status ===
                                        "recurring"
                                            ? theme.colors.success
                                            : theme.colors.light,
                                    color: theme.colors.white,
                                    alignSelf: "flex-start",
                                    borderRadius: 6,
                                    fontSize: 10,
                                    marginTop: 3,
                                }}
                                count={
                                    row?.subscription_state?.status ===
                                    "recurring"
                                        ? "ACTIF"
                                        : "INACTIF"
                                }
                                size={23}
                            />
                        </View>
                    </>
                </TouchableRippleM>
            ),
        },
        {
            name: "Classe",
            style: {
                width: 160,
            },
            selector: (row) => row?.profile?.class ?? "-",
            sortable: true,
            cell: (row) => (
                <View style={{ alignItems: "flex-start" }}>
                    <View
                        style={{
                            marginLeft: 5,
                            flex: 1,
                        }}
                    >
                        <TextM
                            fontWeight="Medium"
                            style={{ marginBottom: 3 }}
                            fontSize={12}
                        >
                            {row?.profile?.class}
                        </TextM>

                        <PrioritizedSchoolSubjects
                            student={row}
                            warningMode
                            ellipsize
                            iconWidth={10}
                            fontSize={9}
                            style={{}}
                        />
                    </View>
                </View>
            ),
        },
        {
            name: "Masteur",
            style: {
                width: 150,
                marginRight: 20,
            },
            selector: (row) =>
                row.tutor
                    ? row.tutor?.display_name
                    : hasRole(row, "premium-student")
                      ? "_"
                      : "-",
            sortable: true,
            // Read-only for the school: the buttons keep the back-office look
            // (tutor name, or a red "Tuteur" when none is assigned yet) but are disabled.
            cell: (row) => {
                return row?.tutor ? (
                    <View>
                        <FilledButtonM
                            label={`${row.tutor?.display_name}`}
                            color={theme.colors.link}
                            style={{
                                width: "100%",
                                marginTop: 7,
                            }}
                            disabled
                            mode={"text"}
                            labelStyle={{ fontSize: 12 }}
                            contentStyle={{
                                height: 25,
                                alignSelf: "flex-start",
                            }}
                        />
                    </View>
                ) : tutoredStudentRoles.some((role) => hasRole(row, role)) ? (
                    <View>
                        <FilledButtonM
                            label={"Tuteur"}
                            icon={"user-plus"}
                            disabled
                            color={theme.colors.error}
                            style={{
                                width: "100%",
                                marginTop: 7,
                                alignSelf: "center",
                            }}
                            labelStyle={{ fontSize: 10 }}
                            contentStyle={{ height: 25 }}
                        />
                    </View>
                ) : row?.current_offer !== "basic" ? (
                    <FilledButtonM
                        disabled
                        label={"Tuteur"}
                        icon={"user-plus"}
                        color={theme.colors.error}
                        style={{
                            width: "100%",
                            marginTop: 7,
                        }}
                        labelStyle={{ fontSize: 10 }}
                        contentStyle={{ height: 25 }}
                    />
                ) : (
                    <TextM>-</TextM>
                )
            },
        },
        {
            name: "Lives",
            style: {
                width: 180,
                maxWidth: 180,
                marginRight: 30,
            },
            selector: "live_sessions_count",
            sortable: true,
            cell: (row) => {
                const isBlocked = row?.is_payment_blocked

                return (
                    <View style={{ alignItems: "flex-start" }}>
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                            }}
                        >
                            <BadgeM
                                style={{
                                    backgroundColor: isBlocked
                                        ? theme.colors.warningLight
                                        : row.live_sessions_count
                                          ? theme.colors.primaryLighter
                                          : theme.colors.lightMore,
                                    color: isBlocked
                                        ? theme.colors.warning
                                        : row.live_sessions?.length
                                          ? theme.colors.primary
                                          : theme.colors.greyAlt,
                                }}
                                count={
                                    isBlocked ? (
                                        <View
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                gap: 4,
                                            }}
                                        >
                                            <FontAwesome5
                                                name="lock"
                                                size={10}
                                                color={theme.colors.warning}
                                            />
                                            <TextM
                                                fontSize={11}
                                                fontWeight="SemiBold"
                                                color={theme.colors.warning}
                                            >
                                                {row.live_sessions_count}
                                            </TextM>
                                        </View>
                                    ) : (
                                        row.live_sessions_count
                                    )
                                }
                                size={23}
                            />
                        </View>
                        {row?.live_sessions?.map((liveSession) => (
                            <LiveBadgeM
                                key={liveSession?.id}
                                liveSession={liveSession}
                                user={row}
                                isPaymentBlocked={row?.is_payment_blocked}
                            />
                        ))}
                    </View>
                )
            },
        },
        {
            name: "Créneaux dispo",
            style: {
                width: 160,
                maxWidth: 160,
                marginRight: 30,
            },
            selector: (row) => row?.slots_possible,
            sortable: true,
            cell: (row) => {
                return (
                    <View>
                        {row?.slots_possible?.length > 0 ? (
                            <AvailabilitySlotsM slots={row?.slots_possible} />
                        ) : (
                            <TextM color={theme.colors.grey}>-</TextM>
                        )}
                    </View>
                )
            },
        },
        {
            name: "Présence en live",
            style: {
                width: 100,
                marginRight: 20,
            },
            selector: "live_reports",
            sortable: true,
            cell: (row) => {
                return (
                    <View>
                        <TextM fontWeight={"Bold"} color={theme.colors.grey}>
                            {row?.statistics?.[statsPeriod]?.engagement
                                ?.live_reports ?? "-"}
                            /
                            {row?.statistics?.[statsPeriod]?.engagement
                                ?.total_live_reports ?? "-"}
                        </TextM>
                        <LinkTextM
                            style={{
                                fontSize: 10,
                                marginTop: 4,
                            }}
                            onPress={() => {
                                navigation.push("live-reports", {
                                    studentId: row?.id,
                                    userId: row?.id,
                                })
                            }}
                        >
                            Voir rapports
                        </LinkTextM>
                    </View>
                )
            },
        },
        {
            name: "Performance en Live",
            style: {
                width: 600,
                marginRight: 40,
            },
            selector: "average_engagement_score",
            sortable: true,
            cell: (row, index) => {
                return !row?.statistics ? (
                    "-"
                ) : (
                    <View>
                        <EngagementScoreComponent
                            totalScore={
                                row?.statistics?.[statsPeriod]?.engagement
                                    ?.scores?.total?.result
                            }
                            disableScoreButton
                            width={600}
                            scores={
                                row?.statistics?.[statsPeriod]?.engagement
                                    ?.scores
                            }
                        />
                        {anonymous && (
                            <TextM fontSize={10} wrap style={{ width: "86%" }}>
                                {fakeComments?.[index % 8]}
                            </TextM>
                        )}
                    </View>
                )
            },
        },
        {
            name: "Gestes",
            style: {
                width: 300,
                marginRight: 40,
            },
            selector: "gestures",
            sortable: true,
            cell: (row) => {
                return (
                    <View
                        style={{
                            flexWrap: "wrap",
                            flexDirection: "row",
                            alignItems: "center",
                            height: 60,
                        }}
                    >
                        <StatisticsItem
                            source={mealsImage}
                            color={theme.colors.warning}
                            count={
                                row?.statistics?.[statsPeriod]?.engagement
                                    ?.meals_offered_count ?? 0
                            }
                            style={{ width: null }}
                        />
                        <StatisticsItem
                            source={treesImage}
                            color={theme.colors.petalDarker}
                            count={
                                row?.statistics?.[statsPeriod]?.engagement
                                    ?.trees_planted_count ?? 0
                            }
                            style={{ width: null }}
                        />
                        <StatisticsItem
                            source={petal}
                            color={theme.colors.petalNew}
                            count={row?.petals ?? 0}
                            style={{ width: null }}
                        />
                    </View>
                )
            },
        },
        {
            id: "account",
            name: "Compte",
            style: {
                width: 160,
            },
            selector: "created_at",
            sortable: true,
            cell: (row) => (
                <View>
                    <BadgeM
                        style={{
                            backgroundColor: theme.colors.greyBg,
                            color: theme.colors.greyAlt,
                            alignSelf: "flex-start",
                            marginTop: 5,
                            marginBottom: 3,
                        }}
                        count={`Créé ${moment(row?.created_at)?.format(
                            "ddd DD MMMM"
                        )}`}
                        size={23}
                    />
                    {row?.is_verified === true ? (
                        <BadgeM
                            style={{
                                backgroundColor: theme.colors.successLight,
                                color: theme.colors.success,
                                alignSelf: "flex-start",
                                marginBottom: 5,
                            }}
                            count={"Vérifié"}
                            size={23}
                        />
                    ) : (
                        <BadgeM
                            style={{
                                color: theme.colors.warning,
                                backgroundColor: theme.colors.warningLight,
                                alignSelf: "flex-start",
                                marginBottom: 5,
                            }}
                            count={"Non vérifié"}
                            size={23}
                        />
                    )}
                </View>
            ),
        },
    ]

    return (
        <PaginatedTableM
            data={props.users}
            flatListRef={null}
            columns={columns}
            requestService={requestApi}
            routeParams={routeParams ?? {}}
            additionalHeader={additionalHeader}
            urlParams={urlParams}
        />
    )
}

const mapStateToProps = (state) => {
    return {
        users: state.data?.users?.usersList,
    }
}

export default connect(mapStateToProps)(SchoolStudentsTable)
